import type { RailwayTransferOption, RailwayTransferPlan, RailwayTransferSearchResult, TransportOption } from "../types";
import { addIsoDays, isPurchasableSeat, selection } from "./itineraryTimetableService";

/** 中转必须有两腿真实身份与完整时刻，缺主数据时仍展示但不可应用。 */
export function railwayTransferUnavailableReason(option: RailwayTransferOption): string {
  if (option.legs.length !== 2 || option.legs.some((leg) => !leg.routeCode || !leg.fromName || !leg.toName
    || !leg.departureAt || !leg.arrivalAt)) return "班次信息不完整，请重新查询";
  if (option.legs.some((leg) => !leg.fromStationId || !leg.toStationId)) return "有车站未匹配启用主数据，请维护车站后重新查询";
  if (option.legs.some((leg) => leg.seats.length > 0 && !leg.seats.some(isPurchasableSeat))) return "部分车次暂无可选席别，请重新查询";
  return "";
}

/** 使用已选席别，否则采用该腿首个有票席别；无席别的时刻候选仍可作为参考。 */
export function railwayTransferSeat(leg: TransportOption, seatClasses: Record<string, string>) {
  const available = leg.seats.filter(isPurchasableSeat);
  return available.find((seat) => seat.seatClass === seatClasses[leg.optionId]) ?? available[0];
}

/** 主备身份由完整两腿与席别决定；保存恢复和重查报价不产生新的同方案身份。 */
export function railwayTransferSelectionId(option: RailwayTransferOption, seatClasses: Record<string, string>) {
  return JSON.stringify(option.legs.map((leg) => [leg.provider, leg.optionId, leg.fromStationId, leg.toStationId,
    leg.departureAt?.slice(0, 19), leg.arrivalAt?.slice(0, 19), railwayTransferSeat(leg, seatClasses)?.seatClass ?? ""]));
}

/** 两腿使用同一个日历日偏移，查询快照完整保留原日期。 */
export function createRailwayTransferPlan(
  option: RailwayTransferOption, result: RailwayTransferSearchResult, seatClasses: Record<string, string>,
  context: { travelDate?: string | null; queryKey: string; queriedAt: string; queryScope: "station" | "region" },
): RailwayTransferPlan {
  const unavailable = railwayTransferUnavailableReason(option);
  if (unavailable) throw new Error(unavailable);
  const originals = option.legs.map((leg) => selection(leg, railwayTransferSeat(leg, seatClasses)));
  const sourceDate = originals[0].departureAt!.slice(0, 10);
  const offset = context.travelDate
    ? Math.round((Date.parse(`${context.travelDate}T00:00:00Z`) - Date.parse(`${sourceDate}T00:00:00Z`)) / 86_400_000)
    : 0;
  if (!Number.isFinite(offset)) throw new Error("中转班次日期无效，请重新查询");
  const shift = (value: string | null | undefined) => value
    ? `${addIsoDays(value.slice(0, 10), offset)}${value.slice(10)}` : value;
  return {
    legs: originals.map((leg) => ({ ...leg, departureAt: shift(leg.departureAt), arrivalAt: shift(leg.arrivalAt) })),
    snapshot: { provider: "py12306", queriedAt: context.queriedAt,
      // 异步归属键含界面标题和拓扑，只在 runtime 使用；持久化保留稳定查询身份。
      queryKey: `transfer:${originals[0].fromStationId}:${originals[originals.length - 1].toStationId}:${result.queryDate}:${context.queryScope}`,
      railwayQueryScope: context.queryScope, options: originals, isReference: result.isReference,
      referenceDate: result.referenceDate, warnings: [...result.warnings,
        ...(offset ? [`查询日 ${result.queryDate} 仅用于参考，已按行程日 ${context.travelDate} 写入班次时刻`] : [])] },
  };
}

/** 已保存整条方案独立恢复，价格与席别沿用选中时快照，不依赖实时查询。 */
export function savedRailwayTransferSelections(plan?: RailwayTransferPlan) {
  if (!plan) return [];
  return [plan, ...(plan.backups ?? [])].map((entry, index) => {
    const legs: TransportOption[] = (entry.snapshot.options.length === 2 ? entry.snapshot.options : entry.legs).map((leg) => ({
      ...leg, provider: "py12306", price: leg.price == null ? null : Number(leg.price), routeCode: leg.routeCode ?? "", seats: leg.seatClass ? [{ seatClass: leg.seatClass,
        price: leg.price == null ? null : Number(leg.price), availability: "已保存方案" }] : [],
    }));
    const first = legs[0]; const last = legs.at(-1)!;
    const difference = (start?: string | null, end?: string | null) => Math.max(0, Math.round((Date.parse(end ?? "") - Date.parse(start ?? "")) / 60_000));
    const queryDate = first.departureAt!.slice(0, 10);
    const option: RailwayTransferOption = {
      optionId: `saved-transfer:${index}:${entry.snapshot.queryKey}`, label: `${first.routeCode} → ${last.routeCode}（${first.toName}${first.toStationId === last.fromStationId ? "" : ` → ${last.fromName}`}）`,
      departureAt: first.departureAt!, arrivalAt: last.arrivalAt!, legs,
      durationMinutes: difference(first.departureAt, last.arrivalAt), waitMinutes: difference(first.arrivalAt, last.departureAt),
      sameStation: first.toStationId === last.fromStationId, sameTrain: first.routeCode === last.routeCode,
    };
    const result: RailwayTransferSearchResult = { requestedDate: queryDate, queryDate, isReference: entry.snapshot.isReference,
      referenceDate: entry.snapshot.referenceDate, warnings: entry.snapshot.warnings, transfers: [option] };
    const seatClasses = Object.fromEntries(legs.map((leg) => [leg.optionId, leg.seats[0]?.seatClass ?? ""]));
    return { id: railwayTransferSelectionId(option, seatClasses), option, result, queriedAt: entry.snapshot.queriedAt, seatClasses };
  });
}
